import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useForm } from "../FormContext";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { Calendar, ChevronLeft, ChevronRight, Clock, X } from "lucide-react";

interface DatePickerFieldProps {
  name: string;
  value?: Dayjs | string;
  onChange?: (value: Dayjs | null) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  showTime?: boolean;
  disabled?: boolean;
  format?: string;
  insideModal?: boolean;
}

export const DatePickerFieldComponent: React.FC<DatePickerFieldProps> = ({
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  required,
  className = "",
  showTime,
  disabled,
  format,
  insideModal = false,
}) => {
  const { setFieldValue, setFieldTouched, validateField } = useForm();

  // Parse the value into a Dayjs object if it's a string
  const parsedValue: Dayjs | null = value ? (typeof value === 'string' ? dayjs(value) : value) : null;

  const handleChange = (newValue: Dayjs | null) => {
    if (newValue) {
      // Format the date according to the specified format
      const formattedDate = newValue.format(format || "YYYY-MM-DD");
      setFieldValue(name, formattedDate);
      if (onChange) {
        onChange(newValue);
      }
    } else {
      setFieldValue(name, null);
      if (onChange) {
        onChange(null);
      }
    }
  };

  const handleBlur = () => {
    setFieldTouched(name, true);
    validateField(name);
    if (onBlur) {
      onBlur();
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const [localDate, setLocalDate] = useState<Dayjs | null>(parsedValue);
  const [localTime, setLocalTime] = useState<{
    hour: number;
    minute: number;
    second: number;
  }>(() => {
    if (parsedValue) {
      return {
        hour: parsedValue.hour(),
        minute: parsedValue.minute(),
        second: parsedValue.second()
      };
    }
    return { hour: 0, minute: 0, second: 0 };
  });
  const [view, setView] = useState<"date" | "month" | "year" | "time">("date");
  const [hoveredDate, setHoveredDate] = useState<Dayjs | null>(null);
  const [calendarPosition, setCalendarPosition] = useState<"top" | "bottom">("bottom");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const calendarContentRef = useRef<HTMLDivElement>(null);
  const { fields } = useForm();
  const fieldValue = fields[name]?.value || value;
  const fieldErrors = fields[name]?.errors || [];
  const hasError = fieldErrors.length > 0;

  // Default format if not provided
  const displayFormat =
    format || (showTime ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD");
    
  // Memoize the close dropdown handler
  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    handleBlur();
  }, [handleBlur]);

  // Calculate current month days, prev month days and next month days
  const getCurrentMonthDays = (date: Dayjs) => {
    const daysInMonth = date.daysInMonth();
    const firstDayOfMonth = date.startOf("month").day(); // 0 is Sunday
    const days: Array<{ date: Dayjs; isCurrentMonth: boolean }> = [];

    // Previous month days
    const prevMonth = date.subtract(1, "month");
    const daysInPrevMonth = prevMonth.daysInMonth();
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push({
        date: prevMonth.date(daysInPrevMonth - firstDayOfMonth + i + 1),
        isCurrentMonth: false,
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: date.date(i),
        isCurrentMonth: true,
      });
    }

    // Next month days
    const totalDaysShown = 42; // 6 rows of 7 days
    const remainingDays = totalDaysShown - days.length;
    const nextMonth = date.add(1, "month");
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: nextMonth.date(i),
        isCurrentMonth: false,
      });
    }

    return days;
  };

  // Get years for year picker (10 years range)
  const getYearRange = (date: Dayjs) => {
    const currentYear = date.year();
    const startYear = Math.floor(currentYear / 10) * 10;
    const years = [];

    for (let i = 0; i < 12; i++) {
      years.push(startYear + i - 1);
    }

    return years;
  };

  // Get formatted display value
  const getDisplayValue = () => {
    if (!localDate) return "";
    if (showTime) {
      const dateWithTime = localDate
        .hour(localTime.hour)
        .minute(localTime.minute)
        .second(localTime.second);
      return dateWithTime.format(displayFormat);
    }
    return localDate.format(displayFormat);
  };

  // Apply date and time selection
  const applyDateTime = () => {
    if (!localDate) return null;

    let result = localDate;
    if (showTime) {
      result = result
        .hour(localTime.hour)
        .minute(localTime.minute)
        .second(localTime.second);
    }

    return result;
  };

  // Calculate dropdown position with enhanced modal support
  const calculateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Set a fixed height for the calendar dropdown (can be adjusted based on content)
    const dropdownHeight = view === 'time' ? 400 : 350; // Increased height for time picker
    
    // Check if there's enough space below
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    
    let top = buttonRect.bottom + 4; // Small gap
    let left = buttonRect.left;
    const width = Math.max(buttonRect.width, 300); // Ensure calendar has minimum width

    // Positioning logic with improved edge case handling
    if (spaceBelow < dropdownHeight) {
      // If there's not enough space below, try to position above
      if (spaceAbove > dropdownHeight) {
        top = buttonRect.top - dropdownHeight - 4; // Position above with gap
      } else {
        // If there's not enough space above either, position it to show as much as possible
        const maxVisibleHeight = Math.max(spaceBelow, spaceAbove);
        if (maxVisibleHeight < 100) {
          // If we can't show at least 100px, position it at the top of the viewport
          top = 10;
        } else {
          // Position it to show as much as possible
          top = Math.min(
            Math.max(10, buttonRect.top - maxVisibleHeight + 4),
            viewportHeight - maxVisibleHeight - 10
          );
        }
      }
    }

    // If inside a modal, ensure the dropdown stays within viewport boundaries
    if (insideModal) {
      // Ensure dropdown doesn't go off screen to the right
      if (left + width > viewportWidth - 10) {
        left = viewportWidth - width - 10;
      }
      
      // Ensure dropdown doesn't go off screen to the left
      if (left < 10) {
        left = 10;
      }
      
      // Make sure dropdown is visible at the bottom
      if (top + dropdownHeight > viewportHeight - 10) {
        top = viewportHeight - dropdownHeight - 10;
      }
      
      // Make sure dropdown is visible at the top
      if (top < 10) {
        top = 10;
      }
    }

    setDropdownPosition({
      top,
      left,
      width,
    });
  }, [view, insideModal]);
  
  // Recalculate position when dropdown opens or view changes
  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
      
      const handleResize = () => calculateDropdownPosition();
      
      // Use requestAnimationFrame to ensure smooth updates during scrolling
      const handleScrollRAF = () => {
        requestAnimationFrame(calculateDropdownPosition);
      };
      
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScrollRAF, true);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScrollRAF, true);
      };
    }
  }, [isOpen, calculateDropdownPosition, view]);

  // Handle clicking outside to close dropdown
  // Handle clicks outside using a capture phase listener
  useEffect(() => {
    if (!isOpen) return;

    function handleGlobalClick(e: MouseEvent) {
      // Get references to current DOM elements
      const selectEl = wrapperRef.current;
      const dropdownEl = dropdownRef.current;
      const targetEl = e.target as Node;
      
      // Check if click is outside both elements
      const isOutsideSelect = selectEl && !selectEl.contains(targetEl);
      const isOutsideDropdown = dropdownEl && !dropdownEl.contains(targetEl);
      
      if (isOutsideSelect && isOutsideDropdown) {
        // Close the dropdown asynchronously to avoid React state update conflicts
        setTimeout(() => closeDropdown(), 0);
      }
    }

    // Use capture phase to get event before it reaches other handlers
    document.addEventListener('click', handleGlobalClick, true);
    
    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
    };
  }, [isOpen, closeDropdown]);
  
  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;
    
    function handleEscapeKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        closeDropdown();
      }
    }
    
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, closeDropdown]);

  // Update local value when prop value changes
  useEffect(() => {
    const newParsedValue = value ? (typeof value === 'string' ? dayjs(value) : value) : null;
    setLocalDate(newParsedValue);
    if (newParsedValue) {
      setLocalTime({
        hour: newParsedValue.hour(),
        minute: newParsedValue.minute(),
        second: newParsedValue.second()
      });
    }
  }, [value]);

  // Clear the selected date
  const clearDate = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalDate(null);
    handleChange(null);
  };

  // Apply and close the picker
  const handleApply = () => {
    const result = applyDateTime();
    handleChange(result);
    setIsOpen(false);
  };

  // Handle date selection
  const handleDateClick = (date: Dayjs) => {
    setLocalDate(date);
    if (!showTime) {
      setTimeout(() => {
        handleChange(date);
        setIsOpen(false);
      }, 100);
    }
  };

  // Navigate to previous/next month
  const navigateMonth = (direction: "prev" | "next") => {
    const dateToNavigate = localDate || dayjs();
    setLocalDate(
      direction === "prev"
        ? dateToNavigate.subtract(1, "month")
        : dateToNavigate.add(1, "month"),
    );
  };

  // Navigate to previous/next year
  const navigateYear = (direction: "prev" | "next") => {
    const dateToNavigate = localDate || dayjs();
    setLocalDate(
      direction === "prev"
        ? dateToNavigate.subtract(1, "year")
        : dateToNavigate.add(1, "year"),
    );
  };

  // Navigate to previous/next decade
  const navigateDecade = (direction: "prev" | "next") => {
    const dateToNavigate = localDate || dayjs();
    setLocalDate(
      direction === "prev"
        ? dateToNavigate.subtract(10, "year")
        : dateToNavigate.add(10, "year"),
    );
  };

  // Check if a date is selected
  const isDateSelected = (date: Dayjs) => {
    if (!localDate) return false;
    return date.format("YYYY-MM-DD") === localDate.format("YYYY-MM-DD");
  };

  // Check if a date is today
  const isToday = (date: Dayjs) => {
    return date.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD");
  };

  // Check if a date is hovered
  const isDateHovered = (date: Dayjs) => {
    if (!hoveredDate) return false;
    return date.format("YYYY-MM-DD") === hoveredDate.format("YYYY-MM-DD");
  };

  // Handle time change
  const handleTimeChange = (
    type: "hour" | "minute" | "second",
    value: number,
  ) => {
    setLocalTime((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  return (
    <div className="relative" ref={wrapperRef}>
      {/* Date Input */}
      <div
        ref={buttonRef}
        onClick={() => {
          if (!disabled) {
            // Calculate position before opening to avoid flicker
            if (!isOpen) {
              calculateDropdownPosition();
            }
            setIsOpen(!isOpen);
          }
        }}
        className={`flex items-center justify-between min-h-[32px] w-full px-2 py-1.5 text-sm border rounded-md cursor-pointer 
          ${disabled ? "bg-gray-100 dark:bg-gray-700 cursor-not-allowed" : ""} 
          ${isOpen ? "border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800" : "border-gray-300 dark:border-gray-700"} 
          ${hasError ? "border-red-500" : ""}
          focus:outline-none dark:text-white transition-all duration-150 ${className}`}
      >
        <span
          className={`${!fieldValue && placeholder ? "text-gray-400 dark:text-gray-500" : "dark:text-white"}`}
        >
          {getDisplayValue() || placeholder || "Select date"}
        </span>
        <div className="flex items-center">
          {fieldValue && (
            <X
              size={16}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer mr-2"
              onClick={clearDate}
            />
          )}
          <Calendar size={16} className="text-black dark:text-white" />
        </div>
      </div>

      {/* Date Picker Dropdown */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[99999] min-w-[300px] bg-white dark:bg-darkCardBackground border border-gray-200 dark:border-gray-700 rounded-md shadow-lg"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px || 'min(90vw, 320px)'`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            ref={calendarContentRef}
            className="p-4"
          >
            {/* Header with navigation */}
            <div className="flex items-center justify-between mb-2">
              {view === "date" && (
                <>
                  <button
                    type="button"
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                    onClick={() => navigateMonth("prev")}
                  >
                    <ChevronLeft size={16} className="dark:text-white"/>
                  </button>
                  <button
                    type="button"
                    className="font-medium dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 px-2 py-1 rounded"
                    onClick={() => setView("month")}
                  >
                    {localDate
                      ? localDate.format("MMMM YYYY")
                      : dayjs().format("MMMM YYYY")}
                  </button>
                  <button
                    type="button"
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                    onClick={() => navigateMonth("next")}
                  >
                    <ChevronRight size={16} className="dark:text-white" />
                  </button>
                </>
              )}

              {view === "month" && (
                <>
                  <button
                    type="button"
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                    onClick={() => navigateYear("prev")}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    type="button"
                    className="font-medium dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 px-2 py-1 rounded"
                    onClick={() => setView("year")}
                  >
                    {localDate
                      ? localDate.format("YYYY")
                      : dayjs().format("YYYY")}
                  </button>
                  <button
                    type="button"
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                    onClick={() => navigateYear("next")}
                  >
                    <ChevronRight size={16} />
                  </button>
                </>
              )}

              {view === "year" && (
                <>
                  <button
                    type="button"
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                    onClick={() => navigateDecade("prev")}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button type="button" className="font-medium dark:text-white">
                    {localDate
                      ? `${Math.floor(localDate.year() / 10) * 10} - ${Math.floor(localDate.year() / 10) * 10 + 9}`
                      : `${Math.floor(dayjs().year() / 10) * 10} - ${Math.floor(dayjs().year() / 10) * 10 + 9}`}
                  </button>
                  <button
                    type="button"
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                    onClick={() => navigateDecade("next")}
                  >
                    <ChevronRight size={16} />
                  </button>
                </>
              )}

              {view === "time" && (
                <div className="w-full text-center font-medium dark:text-white">
                  <button
                    type="button"
                    className="hover:bg-gray-100 dark:hover:bg-gray-700 px-2 py-1 rounded"
                    onClick={() => setView("date")}
                  >
                    {localDate ? localDate.format("YYYY-MM-DD") : "Select Time"}
                  </button>
                </div>
              )}
            </div>

            {/* Calendar Views */}
            {view === "date" && (
              <>
                {/* Day names */}
                <div className="grid grid-cols-7 mb-1">
                  {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                    <div
                      key={day}
                      className="text-center text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar days */}
                <div className="grid grid-cols-7 gap-1">
                  {getCurrentMonthDays(localDate || dayjs()).map(
                    (day, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`
                        h-8 w-8 rounded-md flex items-center justify-center text-sm
                        ${!day.isCurrentMonth ? "text-gray-400 dark:text-gray-600" : "dark:text-gray-200"}
                        ${isDateSelected(day.date) ? "bg-primary text-white hover:bg-blue-600" : ""}
                        ${isToday(day.date) && !isDateSelected(day.date) ? "border border-blue-500" : ""}
                        ${!isDateSelected(day.date) ? "hover:bg-gray-100 dark:hover:bg-gray-700" : ""}
                        ${isDateHovered(day.date) && !isDateSelected(day.date) ? "bg-gray-100 dark:bg-gray-700" : ""}
                      `}
                        onClick={() => handleDateClick(day.date)}
                        onMouseEnter={() => setHoveredDate(day.date)}
                        onMouseLeave={() => setHoveredDate(null)}
                      >
                        {day.date.date()}
                      </button>
                    ),
                  )}
                </div>
              </>
            )}

            {/* Month selection */}
            {view === "month" && (
              <div className="grid grid-cols-4 gap-1 p-2">
                {[
                  "Jan",
                  "Feb",
                  "Mar",
                  "Apr",
                  "May",
                  "Jun",
                  "Jul",
                  "Aug",
                  "Sep",
                  "Oct",
                  "Nov",
                  "Dec",
                ].map((month, index) => {
                  const currentDate = localDate || dayjs();
                  const monthDate = currentDate.month(index);
                  const isSelected =
                    localDate &&
                    localDate.month() === index &&
                    localDate.year() === monthDate.year();

                  return (
                    <button
                      key={month}
                      type="button"
                      className={`
                        py-2 rounded-md text-sm font-medium
                        ${isSelected ? "bg-primary text-white" : "hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200"}
                      `}
                      onClick={() => {
                        setLocalDate(monthDate);
                        setView("date");
                      }}
                    >
                      {month}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Year selection */}
            {view === "year" && (
              <div className="grid grid-cols-3 gap-1 p-2">
                {getYearRange(localDate || dayjs()).map((year) => {
                  const currentDate = localDate || dayjs();
                  const yearDate = currentDate.year(year);
                  const isSelected = localDate && localDate.year() === year;
                  const isOutsideDecade =
                    year < Math.floor(currentDate.year() / 10) * 10 ||
                    year > Math.floor(currentDate.year() / 10) * 10 + 9;

                  return (
                    <button
                      key={year}
                      type="button"
                      className={`
                        py-2 rounded-md text-sm font-medium
                        ${isSelected ? "bg-primary text-white" : "hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200"}
                        ${isOutsideDecade ? "text-gray-400 dark:text-gray-600" : ""}
                      `}
                      onClick={() => {
                        setLocalDate(yearDate);
                        setView("month");
                      }}
                    >
                      {year}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Time selection */}
            {view === "time" && showTime && (
              <div className="flex flex-col space-y-4 py-2">
                {/* Hours */}
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Hours
                  </div>
                  <div className="grid grid-cols-6 gap-1">
                    {Array.from({ length: 24 }).map((_, hour) => (
                      <button
                        key={hour}
                        type="button"
                        className={`
                          h-8 w-8 rounded-md flex items-center justify-center text-sm
                          ${localTime.hour === hour ? "bg-primary text-white" : "hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200"}
                        `}
                        onClick={() => handleTimeChange("hour", hour)}
                      >
                        {hour}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Minutes */}
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Minutes
                  </div>
                  <div className="grid grid-cols-6 gap-1">
                    {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(
                      (minute) => (
                        <button
                          key={minute}
                          type="button"
                          className={`
                          h-8 w-8 rounded-md flex items-center justify-center text-sm
                          ${localTime.minute === minute ? "bg-primary text-white" : "hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200"}
                        `}
                          onClick={() => handleTimeChange("minute", minute)}
                        >
                          {minute}
                        </button>
                      ),
                    )}
                  </div>
                </div>

                {/* Seconds */}
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Seconds
                  </div>
                  <div className="grid grid-cols-6 gap-1">
                    {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(
                      (second) => (
                        <button
                          key={second}
                          type="button"
                          className={`
                          h-8 w-8 rounded-md flex items-center justify-center text-sm
                          ${localTime.second === second ? "bg-primary text-white" : "hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200"}
                        `}
                          onClick={() => handleTimeChange("second", second)}
                        >
                          {second}
                        </button>
                      ),
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Footer with action buttons */}
            <div className="mt-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-3">
              {showTime ? (
                <button
                  type="button"
                  className="text-sm flex items-center text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                  onClick={() => setView(view === "time" ? "date" : "time")}
                >
                  <Clock size={14} className="mr-1" />
                  {view === "time" ? "Show Calendar" : "Show Time"}
                </button>
              ) : (
                <button
                  type="button"
                  className="text-sm text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                  onClick={() => setLocalDate(dayjs())}
                >
                  Today
                </button>
              )}

              {showTime && (
                <button
                  type="button"
                  className="text-sm font-medium text-white bg-primary hover:bg-blue-600 px-3 py-1.5 rounded-md transition-colors"
                  onClick={handleApply}
                >
                  Apply
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
